import React from 'react';
import { arrayOf, array, bool, node, string, func, object } from 'prop-types';
import { propTypes } from '../../../util/types';
import { userDisplayNameAsString } from '../../../util/data';
import { FormattedMessage } from '../../../util/reactIntl';
import { NamedLink, AvatarSmall } from '../../../components';
import classNames from 'classnames';

import CategoryBreadcrumb from './CategoryBreadcrumb/CategoryBreadcrumb';
import SubcategoryList from './SubcategoryList/SubcategoryList';

import css from './MainPanelHeader.module.css';

const MAX_AUTHOR_COUNT = 3;

// Component to display a list of unique authors from listings
const AuthorList = ({ listings }) => {
  // Catalog-sourced listings (Supabase materials) have no author.
  const uniqueAuthors = Array.from(
    new Map(listings.filter(l => l.author).map(l => [l.author.id.uuid, l.author])).values()
  );

  if (uniqueAuthors.length === 0) {
    return null;
  }

  // Limit to a maximum of 3 authors
  const displayedAuthors = uniqueAuthors.slice(0, MAX_AUTHOR_COUNT);
  const hasMoreAuthors = uniqueAuthors.length > MAX_AUTHOR_COUNT;

  return (
    <div className={css.authorList}>
      {displayedAuthors.map(author => {
        const displayName = userDisplayNameAsString(author);
        return (
          <NamedLink
            key={author.id.uuid}
            className={css.author}
            name="ProfilePage"
            params={{
              id: author.id.uuid,
            }}
          >
            <AvatarSmall className={css.authorAvatar} user={author} disableProfileLink />
            <span className={css.authorDisplayName}>{displayName}</span>
          </NamedLink>
        );
      })}
      {hasMoreAuthors && (
        <NamedLink className={css.showAllAuthors} name="SearchUsersPage">
          <FormattedMessage id="SearchPage.MainPanelHeader.showAll" />
        </NamedLink>
      )}
    </div>
  );
};

const MainPanelHeader = props => {
  const {
    rootClassName,
    className,
    listings,
    listingCategories,
    urlQueryParams,
    getHandleChangedValueFn,
    sortByComponent,
    isSortByActive,
    children,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const useHistoryPush = true;
  return (
    <div className={classes}>
      <CategoryBreadcrumb
        listingCategories={listingCategories}
        urlQueryParams={urlQueryParams}
        onSubmit={getHandleChangedValueFn(useHistoryPush)}
      />
      <SubcategoryList
        listingCategories={listingCategories}
        urlQueryParams={urlQueryParams}
        onSubmit={getHandleChangedValueFn(useHistoryPush)}
      />
      <div className={css.searchOptions}>
        <AuthorList listings={listings} />
        {isSortByActive ? <div className={css.sortyByWrapper}>{sortByComponent}</div> : null}
      </div>
      {children}
    </div>
  );
};

MainPanelHeader.defaultProps = {
  rootClassName: null,
  className: null,
  getHandleChangedValueFn: null,
  sortByComponent: null,
  isSortByActive: false,
};

MainPanelHeader.propTypes = {
  rootClassName: string,
  className: string,
  listings: arrayOf(propTypes.listing).isRequired,
  listingCategories: array.isRequired,
  urlQueryParams: object.isRequired,
  getHandleChangedValueFn: func.isRequired,
  sortByComponent: node,
  isSortByActive: bool,
};

export default MainPanelHeader;
